import React, { MouseEventHandler, useCallback, useEffect, useState } from 'react'
import styled from 'styled-components'

import { MAX_TIMEOUT_DELAY, useTimeout } from '@hozana/hooks/useTimeout'
import { useDispatch } from '@hozana/redux/hooks'

import { Div } from 'elements/layout/Div'

import { CurrentIntention } from 'modules/intention/components/CurrentIntention'
import { IntentionForm, TIntentionFormProps } from 'modules/intention/components/IntentionForm'
import { useIntentionsWidget } from 'modules/intention/hooks/useIntentionsWidget'
import { useMyIntention } from 'modules/intention/hooks/useMyIntention'
import { clearIntention } from 'modules/intention/routines'

const UserIntentionWrapper = styled(Div)`
  z-index: 1;
`

export type TUserIntentionProps = Pick<TIntentionFormProps, 'landingPageSlug' | 'placeholder' | 'textareaHeight'>

export const UserIntention: TFC<TUserIntentionProps> = ({ landingPageSlug, placeholder, textareaHeight }) => {
  const dispatch = useDispatch()
  const widget = useIntentionsWidget()
  const myIntention = useMyIntention()

  /**
   * Trigger a useEffect update when userIntention changes
   *
   * @return the time (ms) remaining before expiration, or 0 if already expired
   */
  const getExpirationDelay = useCallback(() => {
    if (myIntention) {
      const diff = new Date(myIntention.publishedUntil).getTime() - new Date().getTime()
      if (diff > 0) {
        return diff
      }
    }
    return 0
  }, [myIntention])

  // Wether the user is editing its (existing) intention
  const [isEditing, setIsEditing] = useState(false)
  const [timeBeforeExpiration, setTimeBeforeExpiration] = useState(getExpirationDelay)
  const [isExpired, setIsExpired] = useState(false)
  const [justPublished, setJustPublished] = useState(false)

  // If intention changed, update the timer (getExpiration changes when userIntention changes)
  useEffect(() => setTimeBeforeExpiration(getExpirationDelay), [getExpirationDelay])

  // Update isExpired when the time has come
  useTimeout(
    () => {
      if (myIntention) {
        setIsExpired(true)
      }
    },
    timeBeforeExpiration,
    timeBeforeExpiration > MAX_TIMEOUT_DELAY,
  )

  const toggleEditIntention: MouseEventHandler<HTMLButtonElement> = (e) => {
    e.preventDefault()
    setIsEditing(!isEditing)
    setJustPublished(false)
  }

  // Launch clearIntentionSaga: asking for confirmation + delete
  const handleClearIntention: MouseEventHandler<HTMLButtonElement> = (e) => {
    e.preventDefault()
    dispatch(clearIntention({ widget }))
  }

  // Handle intention deleted (once user confirmed)
  useEffect(() => {
    if (!myIntention) {
      setIsEditing(false)
      setIsExpired(false)
      setTimeBeforeExpiration(0)
    }
  }, [myIntention])

  const onSubmitSuccess: TIntentionFormProps['onSubmitSuccess'] = () => {
    setIsEditing(false)
    setJustPublished(true)
    setIsExpired(false)
  }

  return (
    <UserIntentionWrapper p="10px">
      {myIntention && !isEditing && !isExpired ? (
        <CurrentIntention
          handleClearIntention={handleClearIntention}
          toggleEditIntention={toggleEditIntention}
          justPublished={justPublished}
        />
      ) : (
        <IntentionForm
          charsLeft
          isExpired={isExpired}
          landingPageSlug={landingPageSlug}
          onSubmitSuccess={onSubmitSuccess}
          placeholder={placeholder}
          textareaHeight={textareaHeight}
          onCancel={toggleEditIntention}
        />
      )}
    </UserIntentionWrapper>
  )
}
